import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import type { ClinicMapLive } from "@clinicMap/clinicMapClient";

import { type DisplayView, toDisplayView } from "./examRoomView";

/** Mirrors brian's `DisplayKind` (models/display.py). */
export type DisplayKind = components["schemas"]["DisplayKind"];

export const DISPLAY_KIND_LABEL: Record<DisplayKind, string> = {
  exam_room: "Exam room",
  clinic_board: "Clinic board",
  treatment_board: "Treatment board",
};

/** Mirrors brian `display/schemas.py` `BoardRotation`: seconds per view, 0 leaves it out. */
export type BoardRotation = components["schemas"]["BoardRotation"];
export type DisplayChange = components["schemas"]["DisplayUpdate"];

export type BoardView = "lanes" | "map" | "roster";

export const BOARD_VIEW_LABEL: Record<BoardView, string> = {
  lanes: "Visits",
  map: "Who is where",
  roster: "Clinic roster",
};

/** A clinic board's views in the order it shows them. */
export const BOARD_VIEWS: readonly {
  view: BoardView;
  seconds: keyof BoardRotation;
}[] = [
  { view: "lanes", seconds: "lanes_s" },
  { view: "map", seconds: "map_s" },
  { view: "roster", seconds: "roster_s" },
];

export interface Display {
  id: string;
  name: string;
  kind: DisplayKind;
  roomId: string | null;
  /** A clinic board's; null for other kinds. */
  rotation: BoardRotation | null;
}

interface DisplayDevice {
  pairedAt: string;
  lastSeenAt: string;
}

export interface ListedDisplay extends Display {
  device: DisplayDevice | null;
}

export interface PairingCode {
  userCode: string;
  pollingSecret: string;
  expiresInSeconds: number;
  intervalSeconds: number;
}

type PairingPoll =
  | { status: "pending" }
  | { status: "paired" }
  | { status: "expired" };

const toDisplay = (d: components["schemas"]["DisplayOut"]): Display => ({
  id: d.uuid,
  name: d.name,
  kind: d.kind,
  roomId: d.room_uuid ?? null,
  rotation: d.rotation ?? null,
});

const toListedDisplay = (
  d: components["schemas"]["DisplayListItem"],
): ListedDisplay => ({
  ...toDisplay(d),
  device: d.device
    ? { pairedAt: d.device.paired_at, lastSeenAt: d.device.last_seen_at }
    : null,
});

// The display cookie lives on the API host, which is cross-origin (same-site) to the app.
const withCookie = { credentials: "include" } as const;

const screenFailure = (response: Response): ClinicAuthError =>
  new ClinicAuthError(
    response.status === 429 ? "rate_limited" : "unknown",
    `HTTP ${response.status}`,
    response.status,
  );

const screenCall = async <T>(
  call: () => Promise<{ data?: T; response: Response }>,
): Promise<{ data?: T; response: Response }> => {
  try {
    return await call();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ClinicAuthError("network_error");
  }
};

/** Called by the screen itself, authenticated only by the display cookie. */
export const displayScreenClient = {
  /** Null when this browser holds no paired display. */
  me: async (signal?: AbortSignal): Promise<Display | null> => {
    const { data, response } = await screenCall(() =>
      apiClient.GET("/display/me", { ...withCookie, signal }),
    );
    if (response.status === 401) return null;
    if (!data) throw screenFailure(response);
    return toDisplay(data);
  },

  /** What this screen shows; brian decides it from the display's kind and room. */
  view: async (signal?: AbortSignal): Promise<DisplayView> => {
    const { data, response } = await screenCall(() =>
      apiClient.GET("/display/me/view", { ...withCookie, signal }),
    );
    if (!data) throw screenFailure(response);
    return toDisplayView(data);
  },

  /** A clinic board's map view; read every few seconds while it shows. */
  liveMap: async (signal?: AbortSignal): Promise<ClinicMapLive> => {
    const { data, response } = await screenCall(() =>
      apiClient.GET("/display/me/live-map", { ...withCookie, signal }),
    );
    if (!data) throw screenFailure(response);
    return data;
  },

  startPairing: async (signal?: AbortSignal): Promise<PairingCode> => {
    const { data, response } = await screenCall(() =>
      apiClient.POST("/display/pairing", { ...withCookie, signal }),
    );
    if (!data) throw screenFailure(response);
    return {
      userCode: data.user_code,
      pollingSecret: data.polling_secret,
      expiresInSeconds: data.expires_in,
      intervalSeconds: data.interval,
    };
  },

  /** On "paired" the response has set the display cookie. */
  pollPairing: async (
    pollingSecret: string,
    signal?: AbortSignal,
  ): Promise<PairingPoll> => {
    const { data, response } = await screenCall(() =>
      apiClient.POST("/display/pairing/token", {
        ...withCookie,
        body: { polling_secret: pollingSecret },
        signal,
      }),
    );
    if (response.status === 400) return { status: "expired" };
    if (!data) throw screenFailure(response);
    return { status: data.status };
  },
};

export const displaysClient = {
  list: () =>
    request(() => apiClient.GET("/displays", {})).then((rows) =>
      rows.map(toListedDisplay),
    ),

  create: (body: { name: string; kind: DisplayKind; roomId: string | null }) =>
    request(() =>
      apiClient.POST("/displays", {
        body: { name: body.name, kind: body.kind, room_uuid: body.roomId },
      }),
    ).then(toDisplay),

  /** 400 when the code is wrong, used or expired. */
  pair: ({ displayId, userCode }: { displayId: string; userCode: string }) =>
    request(() =>
      apiClient.POST("/displays/pair", {
        body: { display_uuid: displayId, user_code: userCode },
      }),
    ).then(toDisplay),

  /** Leadership only; 422 `rotation_board_only` for a rotation on another kind. */
  update: (displayId: string, change: DisplayChange) =>
    request(() =>
      apiClient.PATCH("/displays/{display_uuid}", {
        params: { path: { display_uuid: displayId } },
        body: change,
      }),
    ).then(toDisplay),

  unpair: (displayId: string) =>
    requestVoid(() =>
      apiClient.POST("/displays/{display_uuid}/unpair", {
        params: { path: { display_uuid: displayId } },
      }),
    ),

  remove: (displayId: string) =>
    requestVoid(() =>
      apiClient.DELETE("/displays/{display_uuid}", {
        params: { path: { display_uuid: displayId } },
      }),
    ),
};
